import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions
} from '@headlessui/react'
import { Check, ChevronDown } from 'lucide-react'
import React from 'react'

export interface Option {
  value: unknown
  label: string
  subLabel?: string
  disabled?: boolean
  icon?: React.ReactNode
}

interface SelectProps {
  options: Option[]
  value?: unknown
  onChange: (value: unknown, option?: Option | Option[]) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  error?: boolean
  multiple?: boolean
}

const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = '请选择...',
  disabled = false,
  className = '',
  error = false,
  multiple = false
}) => {
  const selectedOption = Array.isArray(value)
    ? options.filter((option) => value.includes(option.value))
    : options.find((option) => option.value === value) || null

  const handleChange = (newValue: Option | Option[] | null) => {
    if (Array.isArray(newValue)) {
      const values = newValue.map((opt) => opt.value)
      onChange(values, newValue)
    } else if (newValue) {
      onChange(newValue.value, newValue)
    } else {
      onChange(undefined, undefined)
    }
  }

  const getDisplayValue = () => {
    if (Array.isArray(selectedOption)) {
      if (selectedOption.length === 0) return placeholder
      if (selectedOption.length === 1) {
        return (
          <span className="flex items-center gap-2">
            {selectedOption[0].icon}
            {selectedOption[0].label}
          </span>
        )
      }
      return `已选择 ${selectedOption.length} 项`
    }
    if (selectedOption?.icon) {
      return (
        <span className="flex items-center gap-2">
          {selectedOption.icon}
          {selectedOption.label}
        </span>
      )
    }
    return selectedOption?.label || placeholder
  }

  const isSelected = (option: Option) => {
    if (Array.isArray(value)) {
      return value.includes(option.value)
    }
    return value === option.value
  }

  return (
    <div className={`relative ${className}`}>
      <Listbox
        value={selectedOption || (multiple ? [] : null)}
        onChange={handleChange}
        disabled={disabled}
        multiple={multiple}
      >
        <ListboxButton
          className={`
            relative w-full cursor-default rounded-lg py-2 pl-3 pr-10 text-left shadow-sm
            border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
            ${
              error
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-blue-500'
            }
            ${
              disabled
                ? 'bg-gray-50 text-gray-500 cursor-not-allowed'
                : 'bg-white text-gray-900'
            }
            sm:text-sm
          `}
        >
          <span
            className={`block truncate ${
              !selectedOption ||
              (Array.isArray(selectedOption) && selectedOption.length === 0)
                ? 'text-gray-500'
                : ''
            }`}
          >
            {getDisplayValue()}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDown
              className="h-5 w-5 text-gray-400 transition-transform ui-open:rotate-180"
              aria-hidden="true"
            />
          </span>
        </ListboxButton>

        <ListboxOptions
          anchor="bottom"
          className="w-[var(--button-width)] mt-1 max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm empty:invisible"
        >
          {options.map((option) => (
            <ListboxOption
              key={String(option.value)}
              value={option}
              disabled={option.disabled}
              className={({ focus }) =>
                `relative cursor-default select-none py-2 pl-10 pr-4 ${
                  focus ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                } ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}`
              }
            >
              {({ selected, focus }) => (
                <>
                  <div className="flex items-center gap-2">
                    {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                    <div className="flex flex-col flex-1">
                      <span
                        className={`block truncate ${
                          selected ? 'font-medium' : 'font-normal'
                        }`}
                      >
                        {option.label}
                      </span>
                      {option.subLabel && (
                        <span
                          className={`block truncate text-sm ${
                            focus ? 'text-blue-700' : 'text-gray-500'
                          }`}
                        >
                          {option.subLabel}
                        </span>
                      )}
                    </div>
                  </div>
                  {isSelected(option) ? (
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                      <Check className="h-5 w-5" aria-hidden="true" />
                    </span>
                  ) : null}
                </>
              )}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  )
}

export default Select
